/**
 * The options the menus own (08 §4): volumes, text size, the comfort toggles and the
 * accessibility switches. A tiny observable object; the app reads it (`getMenuSettings()`) and
 * subscribes for changes. Persisted as JSON under one key; a missing or blocked store just means
 * defaults, and settings saved by an older build gain the new fields at their defaults.
 * The control scheme is not here: it belongs to the key bindings (controls/bindingsPort.ts).
 */

export type TextSize = 1 | 1.5 | 2;
/** Ground-phase speed (08 §4): 100 %, 85 %, 70 %. Marked on the record, never blocks progression. */
export type GameSpeed = 1 | 0.85 | 0.7;

export interface Settings {
  /** 0-1. */
  masterVolume: number;
  musicVolume: number;
  sfxVolume: number;
  textSize: TextSize;
  screenShake: boolean;
  /** Muzzle and explosion flashes at full strength; off = photosensitivity-safe. */
  flashes: boolean;
  crtFilter: boolean;
  /** The original's single-box HUD (08 §2.7). */
  hud1987: boolean;
  noiseRing: boolean;
  /** The permadeath line has been shown once (08 §2.4). */
  permadeathSeen: boolean;
  /** Colour-blind-safe accents (05 §4.3): enemy red becomes blue, in the menus and wherever the app follows. */
  colourBlind: boolean;
  /** Long text (briefings, citations, prose) in a system sans-serif with looser spacing. */
  readableFont: boolean;
  gameSpeed: GameSpeed;
  /** Run is a toggle rather than a hold. */
  runToggle: boolean;
  /** Crawl is a toggle rather than a hold. On by default: that is how the key has always worked. */
  crawlToggle: boolean;
  /** ROUND7: the crouched walk (sneak) is a toggle rather than a hold. On by default, like crawl. */
  sneakToggle: boolean;
  /** Directional on-screen pips for gunfire, shouts, vehicles and aircraft (08 §4). */
  soundPips: boolean;
}

export const DEFAULT_SETTINGS: Readonly<Settings> = {
  masterVolume: 0.8,
  musicVolume: 0.6,
  sfxVolume: 0.9,
  textSize: 1,
  screenShake: true,
  flashes: true,
  crtFilter: false,
  hud1987: false,
  noiseRing: true,
  permadeathSeen: false,
  colourBlind: false,
  readableFont: false,
  gameSpeed: 1,
  runToggle: false,
  crawlToggle: true,
  sneakToggle: true,
  soundPips: false,
};

export const SETTINGS_KEY = 'nightdrop.settings.v1';

/** The slice of `localStorage` the store needs, so tests can hand it a plain object. */
export interface KeyValue {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

function unit(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? Math.min(1, Math.max(0, value)) : fallback;
}

function flag(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

/** Turns whatever was stored into valid settings: unknown fields dropped, bad values defaulted. */
export function sanitizeSettings(raw: unknown): Settings {
  const d = DEFAULT_SETTINGS;
  const r: Record<string, unknown> = typeof raw === 'object' && raw !== null ? { ...raw } : {};
  const size = r['textSize'];
  const speed = r['gameSpeed'];
  return {
    masterVolume: unit(r['masterVolume'], d.masterVolume),
    musicVolume: unit(r['musicVolume'], d.musicVolume),
    sfxVolume: unit(r['sfxVolume'], d.sfxVolume),
    textSize: size === 1.5 || size === 2 ? size : 1,
    screenShake: flag(r['screenShake'], d.screenShake),
    flashes: flag(r['flashes'], d.flashes),
    crtFilter: flag(r['crtFilter'], d.crtFilter),
    hud1987: flag(r['hud1987'], d.hud1987),
    noiseRing: flag(r['noiseRing'], d.noiseRing),
    permadeathSeen: flag(r['permadeathSeen'], d.permadeathSeen),
    colourBlind: flag(r['colourBlind'], d.colourBlind),
    readableFont: flag(r['readableFont'], d.readableFont),
    gameSpeed: speed === 0.85 || speed === 0.7 ? speed : 1,
    runToggle: flag(r['runToggle'], d.runToggle),
    crawlToggle: flag(r['crawlToggle'], d.crawlToggle),
    sneakToggle: flag(r['sneakToggle'], d.sneakToggle),
    soundPips: flag(r['soundPips'], d.soundPips),
  };
}

export class SettingsStore {
  private current: Settings;
  private readonly listeners = new Set<(settings: Settings) => void>();

  constructor(private readonly storage: KeyValue | null) {
    let raw: unknown = null;
    try {
      const text = storage?.getItem(SETTINGS_KEY) ?? null;
      if (text !== null) raw = JSON.parse(text);
    } catch {
      raw = null;
    }
    this.current = sanitizeSettings(raw);
  }

  get(): Readonly<Settings> {
    return this.current;
  }

  set(patch: Partial<Settings>): void {
    this.current = sanitizeSettings({ ...this.current, ...patch });
    try {
      this.storage?.setItem(SETTINGS_KEY, JSON.stringify(this.current));
    } catch {
      // Private browsing or a full store: the setting still holds for this session.
    }
    for (const listener of this.listeners) listener(this.current);
  }

  /** Calls `listener` on every change; returns the unsubscribe function. */
  subscribe(listener: (settings: Settings) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }
}

let shared: SettingsStore | null = null;

/** The one store the menus and the app share, backed by `localStorage` where there is one. */
export function getMenuSettings(): SettingsStore {
  if (shared === null) {
    let storage: KeyValue | null = null;
    try {
      storage = typeof window !== 'undefined' ? window.localStorage : null;
    } catch {
      storage = null;
    }
    shared = new SettingsStore(storage);
  }
  return shared;
}
